import { Clock, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MyTruckRow } from "@/hooks/useMyTrucks";

interface StaleTruckPostsPanelProps {
  trucks: MyTruckRow[];
}

const BADGE_STYLES = {
  amber: "bg-[hsl(38_75%_82%)] text-[hsl(30_65%_28%)]",
  red:   "bg-[hsl(0_65%_90%)] text-[hsl(0_60%_35%)]",
  slate: "bg-[hsl(215_18%_83%)] text-[hsl(215_28%_33%)]",
} as const;

type Tone = keyof typeof BADGE_STYLES;

interface StaleRow {
  id: string;
  origin: string;
  destination: string;
  signal: string;
  badge: string;
  tone: Tone;
}

function classifyTruck(t: MyTruckRow): StaleRow | null {
  if (t.status !== "active") return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const availTo = new Date(t.availableTo);
  availTo.setHours(0, 0, 0, 0);
  const daysUntilExpire = Math.round((availTo.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let signal = "";
  let badge = "";
  let tone: Tone = "slate";

  if (daysUntilExpire <= 0) {
    signal = "Expires today";
    badge = "Repost";
    tone = "red";
  } else if (daysUntilExpire === 1) {
    signal = "Expires in 1 day";
    badge = "Refresh";
    tone = "amber";
  } else if (t.ageHours >= 36) {
    signal = `Posted ${Math.floor(t.ageHours)}h ago`;
    badge = "Stale";
    tone = "amber";
  } else {
    return null;
  }

  return {
    id: t.id,
    origin: t.origin,
    destination: t.destination,
    signal,
    badge,
    tone,
  };
}

export function StaleTruckPostsPanel({ trucks }: StaleTruckPostsPanelProps) {
  const stale = trucks.map(classifyTruck).filter((r): r is StaleRow => r !== null).slice(0, 3);

  return (
    <div
      className="flex flex-col h-full rounded-lg border bg-card overflow-hidden"
      style={{ boxShadow: "var(--shadow-sm)" }}
    >
      <div className="flex items-center justify-between border-b px-3 py-2.5 bg-muted/20">
        <div className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-warning" />
          <h4 className="text-caption font-bold text-foreground">Stale Truck Posts</h4>
        </div>
        <span className="text-[10px] text-muted-foreground">{stale.length} flagged</span>
      </div>

      <div className="flex-1 flex flex-col">
        {stale.length === 0 ? (
          <div className="flex-1 flex items-center justify-center px-3 py-6 text-center">
            <div className="space-y-1">
              <RefreshCw className="h-4 w-4 text-muted-foreground mx-auto" />
              <p className="text-[11px] text-muted-foreground leading-snug">
                All your trucks are fresh.<br />We'll flag any nearing expiration.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 divide-y">
            {stale.map(row => (
              <div key={row.id} className="flex items-center justify-between px-3 py-2 gap-2 min-w-0">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold text-foreground truncate">
                    {row.origin} → {row.destination}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{row.signal}</p>
                </div>
                <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0", BADGE_STYLES[row.tone])}>
                  {row.badge}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
